import { has, assign, startsWith, first, split, isNil, includes } from 'lodash';

import * as Sentry from '@sentry/react';
import LogRocket from 'logrocket';
import posthog from 'posthog-js';
import URL from 'url-parse';

import {
  SENTRY_TRACKER_ID,
  LOGROCKET_TRACKER_ID,
  PH_PJ_ID,
  SENTRY_PERFORMANCE_ALLOWED,
  DEPLOY_ENV,
} from '../constants/config';
import { ROUTES_PUBLIC } from '../constants/pathnames';

import { API_SERVER_URL } from '../constants/api';

function getFSAnalytics() {
  return 'undefined' !== typeof window.FS ? window.FS : null;
}

function getSegmentAnalytics() {
  return 'undefined' !== typeof window.analytics ? window.analytics : null;
}

function getPendoAnalytics() {
  return 'undefined' !== typeof window.pendo ? window.pendo : null;
}

function getSentry() {
  if (SENTRY_TRACKER_ID) {
    Sentry.init({
      dsn: SENTRY_TRACKER_ID,
      environment: DEPLOY_ENV,
      ignoreErrors: ['ResizeObserver'],
      integrations: [
        SENTRY_PERFORMANCE_ALLOWED === 'true' ? new Sentry.BrowserTracing() : null,
      ].filter((i) => !!i),
      normalizeDepth: 10,
    });
    return Sentry;
  }
  return null;
}

function registerUserSession(user, userOrgId, org = null) {
  if (!user) {
    return;
  }
  const { email, full_name } = user;
  let appEnvTypeString = '';
  if (startsWith(API_SERVER_URL, 'https://dataops')) {
    appEnvTypeString = 'Prod';
  } else if (startsWith(API_SERVER_URL, 'https://sb')) {
    appEnvTypeString = 'Sandbox';
  } else {
    const parsedUrl = API_SERVER_URL ? new URL(API_SERVER_URL) : API_SERVER_URL;
    appEnvTypeString =
      parsedUrl && has(parsedUrl, 'hostname') ? first(split(parsedUrl.hostname, '.')) : '';
  }

  let segmentAnalytics = getSegmentAnalytics();
  let FSAnalytics = getFSAnalytics();
  let pendoAnalytics = getPendoAnalytics();
  // let sentryAnalytics = getSentry();
  let userIdString = `${user.id}-${appEnvTypeString}`;

  let analyticsMeta = {
    email: email,
    full_name: full_name,
    // org_name: default_org && has(default_org, 'name') ? default_org.name : ''
  };

  analyticsMeta.org_name = org && has(org, 'name') ? org.name : userOrgId;
  analyticsMeta.org_id = userOrgId;
  analyticsMeta.is_external = true;
  analyticsMeta.nexla_env = appEnvTypeString;
  analyticsMeta.self_signup = org?.self_signup ?? false;

  if (
    userOrgId === 1 ||
    (!isNil(org) && has(org, 'email_domain') && org.email_domain === 'nexla.com')
  ) {
    analyticsMeta.is_external = false;
  }

  if (!isNil(pendoAnalytics) && has(pendoAnalytics, 'initialize')) {
    let pendoUser = {
      id: userIdString,
      email: email,
      full_name: full_name,
      is_external: analyticsMeta.is_external,
      nexla_env: appEnvTypeString,
    };
    let pendoAccount = {
      id: `${analyticsMeta.org_id}-${analyticsMeta.org_name}-${appEnvTypeString}`,
      name: analyticsMeta.org_name,
      is_external: analyticsMeta.is_external,
    };

    pendoAnalytics?.initialize({
      vistor: pendoUser,
      account: pendoAccount,
    });
  }

  if (!isNil(segmentAnalytics)) {
    segmentAnalytics?.identify(userIdString, analyticsMeta);

    //now post user login notification
    if (analyticsMeta.is_external) {
      segmentAnalytics?.track('User Session', analyticsMeta);
    }
  }

  if (!isNil(FSAnalytics)) {
    let fsCustomUser = {
      displayName: `${user.full_name} : ${appEnvTypeString}`,
    };
    //only track non-nexla users
    FSAnalytics?.identify(userIdString, assign({}, analyticsMeta, fsCustomUser));
  }

  if (SENTRY_TRACKER_ID) {
    let sentryUser = {
      email,
      id: userIdString,
    };
    Sentry?.configureScope((scope) => {
      scope.setUser(sentryUser);
    });
  }

  LogRocket?.identify(userIdString, {
    name: user.full_name,
    ...analyticsMeta,
  });

  if (PH_PJ_ID && typeof posthog !== 'undefined' && posthog) {
    try {
      posthog.identify(`${email}-${analyticsMeta.org_name}-${userIdString}`, analyticsMeta);
      posthog.startSessionRecording();
    } catch (error) {
      console.warn('PostHog user identification failed:', error);
    }
  }
}

function clearUserSession() {
  let FSAnalytics = getFSAnalytics();
  // let sentryAnalytics = getSentry();

  if (FSAnalytics) {
    FSAnalytics.clearUserCookie();
  }

  if (SENTRY_TRACKER_ID) {
    Sentry.configureScope((scope) => {
      scope.setUser({});
    });
  }

  if (PH_PJ_ID && typeof posthog !== 'undefined' && posthog) {
    try {
      posthog.stopSessionRecording();
      posthog.reset();
    } catch (error) {
      console.warn('PostHog session cleanup failed:', error);
    }
  }
}

function trackPageTransition(newPage, additionalProps = {}) {
  // Null safety check for newPage
  if (!newPage || includes(ROUTES_PUBLIC, newPage)) {
    return;
  }

  //only run if page is not public
  //segment

  let segmentAnalytics = getSegmentAnalytics();
  if (segmentAnalytics) {
    //TODO: suppress page transitions for now.
    //segmentAnalytics.page();
  }

  if (PH_PJ_ID && typeof posthog !== 'undefined' && posthog) {
    try {
      // Use PostHog's proper pageview tracking for SPA
      posthog.capture('$pageview', {
        $current_url: window.location?.href || '',
        $pathname: newPage,
        $title: document?.title || '',
        ...additionalProps,
      });
    } catch (error) {
      console.warn('PostHog pageview capture failed:', error);
    }
  }
}

function captureCustomEvent(eventId = null, eventInfo = {}, options = {}) {
  if (isNil(eventId)) {
    return;
  }

  // Skip if PostHog is not available
  if (!PH_PJ_ID || typeof posthog === 'undefined' || !posthog) {
    return;
  }

  // Ensure eventInfo is an object
  if (!eventInfo || typeof eventInfo !== 'object') {
    eventInfo = {};
  }

  // Ensure options is an object
  if (!options || typeof options !== 'object') {
    options = {};
  }

  // Apply sampling if specified (default: capture all events)
  const sampleRate = options.sampleRate || 1.0;
  if (Math.random() > sampleRate) {
    return;
  }

  // Filter out high-volume, low-value events for product analytics
  const lowValueEvents = ['mouse_move', 'scroll', 'hover', 'focus', 'blur', 'keypress'];
  if (lowValueEvents.includes(eventId) && !options.forceCapture) {
    return;
  }

  // Limit event properties to reduce data size (but allow more for product analytics)
  const maxProperties = options.maxProperties || 15; // Increased for product analytics
  const limitedEventInfo = Object.keys(eventInfo)
    .slice(0, maxProperties)
    .reduce((obj, key) => {
      // Ensure values are serializable and not too large
      const value = eventInfo[key];
      if (value !== null && value !== undefined) {
        try {
          const stringValue = String(value);
          if (stringValue.length <= 1000) {
            // Limit property value size
            obj[key] = value;
          }
        } catch (error) {
          console.warn('Failed to serialize event property:', key, error);
        }
      }
      return obj;
    }, {});

  try {
    posthog.capture(eventId, limitedEventInfo);
  } catch (error) {
    console.warn('PostHog event capture failed:', error);
  }
}

function initLogRocket() {
  if (LOGROCKET_TRACKER_ID) {
    // console.log('initialize lr:' + LOGROCKET_TRACKER_ID);
    LogRocket.init(LOGROCKET_TRACKER_ID, {
      network: {
        isEnabled: false,
      },
      shouldDebugLog: false,
      dom: {
        textSanitizer: true,
      },
    });
  }
}

function initPosthog() {
  if (!PH_PJ_ID || typeof posthog === 'undefined') {
    return;
  }

  try {
    posthog.init(PH_PJ_ID, {
      //api_host: 'https://app.posthog.com',
      autocapture: true,
      capture_pageview: false,
      capture_pageleave: false,
      enable_recording_console_log: false,
      disable_session_recording: true,
      capture_pageview_on_page_load: false,
      capture_pageview_on_page_show: false,
      capture_pageview_on_page_hide: false,
      capture_pageview_on_page_focus: false,
      capture_pageview_on_page_blur: false,
      autocapture: {
        dom_capture: true,
        css_selector_allowlist: [
          'button',
          'a',
          'input[type="submit"]',
          'input[type="button"]',
          '[data-analytics]',
          '[role="button"]',
          '[role="link"]',
        ],
        element_attribute_allowlist: ['data-testid', 'data-analytics', 'id', 'class', 'role'],
        element_attribute_blocklist: ['data-no-analytics'],
        dom_event_capture: ['click', 'submit', 'change', 'input'],
      },
      loaded: (posthog) => {
        // Optional: Enable sampling only if you have very high traffic
        // if (window.location.hostname === 'production.example.com') {
        //   if (Math.random() > 0.8) { // Sample 80% of users
        //     posthog.opt_out_capturing();
        //   }
        // }
      },
    });
  } catch (error) {
    console.warn('PostHog initialization failed:', error);
  }
}

function reportErrorToSentry(error, errorInfo) {
  if (SENTRY_TRACKER_ID) {
    Sentry.withScope((scope) => {
      scope.setExtras(errorInfo);
      Sentry.captureException(error);
    });
  }
}

export default {
  trackPageTransition: trackPageTransition,
  registerUserSession: registerUserSession,
  clearUserSession: clearUserSession,
  getSentry: getSentry,
  initLogRocket: initLogRocket,
  initPosthog: initPosthog,
  reportErrorToSentry: reportErrorToSentry,
  captureCustomEvent: captureCustomEvent,
};
